import React, { useCallback } from 'react';

import { useViewModel } from './useViewModel';

import { highlightInlineText } from '../../utilities/highlightInlineText';

import { PersonalisationHelperModal } from '../PersonalisationHelperModal';

import styles from './personalisation-card.module.css';
import gridStyles from '../PersonalisationStep/personalisation-grid.module.css';
import { ResponsiveImage } from '../ResponsiveImage';

/**
 * PersonalisationCard component displays a product variant card with flexible option rendering and layout.
 *
 * @param {Object} props - Component props
 * @param {string} props.id - The ID of the variant
 * @param {string} props.slotId - The ID of the slot
 * @param {string} props.imageSrc - Image source URL
 * @param {string} props.imageAlt - Alt text for the image
 * @param {string} props.title - Title of the variant/product
 * @param {string|null} props.description - Description (HTML allowed)
 * @param {boolean} props.availableForSale - Whether the variant is available for sale
 * @param {number|string=} props.price - Price of the variant
 * @param {number|string=} props.compareAtPrice - Compare at price (optional)
 * @param {Array} props.variants - Array of variant objects
 * @param {string} props.selectedVariantId - The ID of the selected variant
 * @param {string=} props.layout - "vertical" or "horizontal"
 * @param {string=} props.secondaryOptionsName - Secondary option display group, e.g., "Fine Chain
 * @param {string=} props.secondaryOptionsLabel - Secondary option display label, e.g., "Choose Chain Length"
 * @param {boolean} props.isSelected - Whether the card is selected
 * @param {Object} [props.secondaryOptions] - Secondary options data (optional)
 * @param {function} [props.onSecondarySelect] - Handler for secondary selection (optional)
 * @param {boolean} [props.disableSecondaryOptions] - Whether to disable secondary options (optional)
 * @param {string} [props.badge] - The badge of the component group
 * @param {string} [props.helpPage] - The help page for the component group
 * @param {string} [props.priceModifierType] - The type of price modifier ('PERCENTAGE' or 'AMOUNT')
 * @param {number|string} [props.priceModifierValue] - The value of the price modifier
 * @returns {import('react').JSX.Element}
 */
export function PersonalisationCard(props) {
  const {
    id,
    slotId,
    imageSrc,
    imageAlt,
    title,
    description,
    availableForSale,
    secondaryOptionsName,
    secondaryOptions,
    openModal,
    handleSecondarySelect,
    price,
    originalPrice,
    compareAtPriceObj,
    selectedSecondaryLabel,
    modalOpen,
    setModalOpen,
    hasSecondaryOptions,
    currentSelectionForSlot,
    itemLabel,
    handleSelection,
    handleLabelClick,
    formatPrice,
    badge,
    helpPage,
    helperModalOpen,
    setHelperModalOpen,
    cardInnerWrapperClassName,
    cardInnerClassName
  } = useViewModel(props);

  return (
    <li
      className={`${styles['list-style-none']} ${styles['personalisation__card']}`}
      aria-label={title || 'Personalisation option'}
    >
      {badge && (
        <span className={styles['personalisation__card-badge'] + ' body-2xs body-uppercase font-medium'}>{badge}</span>
      )}
      <input
        readOnly
        id={`${slotId}-${id}`}
        type="radio"
        value={id}
        checked={currentSelectionForSlot === id}
        name={`${slotId}-${title}`}
        className={styles['personalisation__hidden-input']}
        onChange={hasSecondaryOptions ? openModal : handleSelection}
        disabled={!availableForSale}
      />
      <div className={cardInnerWrapperClassName}>
        {helpPage && (
          <>
            <button
              className={styles['personalisation__card-page-helper'] + ' button-reset'}
              aria-label={`Open ${helpPage?.modalTriggerText?.value || helpPage.title}`}
              onClick={() => {
                setHelperModalOpen(true);
              }}
            >
              <span className="svg-wrapper svg-wrapper--small">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.833374 8.00004C0.833374 4.042 4.042 0.833374 8.00004 0.833374C11.958 0.833374 15.1667 4.04199 15.1667 8.00004C15.1667 11.958 11.958 15.1667 8.00004 15.1667C4.04199 15.1667 0.833374 11.958 0.833374 8.00004ZM8.12997 6.68731C8.30704 6.71351 8.53917 6.78351 8.72904 6.99231C8.91064 7.19211 8.96297 7.42057 8.98291 7.58351C9.00017 7.72477 9.00011 7.89104 9.00004 8.04017V8.06671V11C9.00004 11.3682 8.70157 11.6667 8.33337 11.6667C7.96517 11.6667 7.66671 11.3682 7.66671 11V8.06671V8.00004C7.29851 8.00004 7.00004 7.70157 7.00004 7.33337C7.00004 6.96517 7.29851 6.66671 7.66671 6.66671L7.69391 6.6667C7.82604 6.66659 7.98904 6.66647 8.12997 6.68731ZM8.00004 4.66671C7.63184 4.66671 7.33337 4.96519 7.33337 5.33337C7.33337 5.70156 7.63184 6.00004 8.00004 6.00004C8.36824 6.00004 8.66677 5.70156 8.66677 5.33337C8.66677 4.96519 8.36824 4.66671 8.00004 4.66671Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
            <PersonalisationHelperModal
              isOpen={helperModalOpen}
              onClose={() => {
                setHelperModalOpen(false);
              }}
              title={helpPage.title}
            >
              <div className="rte" dangerouslySetInnerHTML={{ __html: helpPage.body }} />
            </PersonalisationHelperModal>
          </>
        )}
        <label htmlFor={`${slotId}-${id}`} className={cardInnerClassName} onClick={handleLabelClick}>
          {imageSrc && (
            <div className={styles['personalisation__card-image-wrapper']}>
              <ResponsiveImage
                src={imageSrc}
                className={styles['personalisation__card-image']}
                alt={imageAlt}
                // @TODO: Create the sizes using the column config.
                sizes={[{ media: '(max-width: 768px)', size: '50vw' }]}
                fallbackSize="100vw"
                decoding="async"
                loading="lazy"
              />
            </div>
          )}
          <div className={styles['personalisation__card-content']}>
            {/* // Remove the secondary option label if we are grouping */}
            {title && <p className={styles['personalisation__card-content-title']}>{itemLabel}</p>}
            {description && <p className="body-xs">{highlightInlineText(description, 'orange')}</p>}
            {typeof price.amount !== 'undefined' && price.amount !== null && (
              <p
                className={styles['personalisation__card-price'] + ' body-xs'}
                aria-label={`Price: ${formatPrice({ amount: price.amount })}${compareAtPriceObj ? `, was ${formatPrice({ amount: compareAtPriceObj.amount })}` : ''}${originalPrice ? `, original price ${formatPrice({ amount: originalPrice.amount })}` : ''}`}
              >
                {!originalPrice && compareAtPriceObj && (
                  <span
                    className={styles['personalisation__card-compare-price'] + ' body-xs'}
                    aria-label={`Was ${formatPrice({ amount: compareAtPriceObj.amount })}`}
                  >
                    {formatPrice({ amount: compareAtPriceObj.amount })}
                  </span>
                )}
                {originalPrice && (
                  <span
                    className={styles['personalisation__card-original-price'] + ' body-xs'}
                    aria-label={`Original price ${formatPrice({ amount: originalPrice.amount })}`}
                  >
                    {formatPrice({ amount: originalPrice.amount })}
                  </span>
                )}
                <span aria-label={`Additional cost ${formatPrice({ amount: price.amount })}`}>
                  +{formatPrice({ amount: price.amount })}
                </span>
              </p>
            )}
          </div>
        </label>

        {secondaryOptions?.length > 1 && (
          <SecondaryOptions
            secondaryOptions={secondaryOptions}
            openModal={openModal}
            selectedSecondaryLabel={selectedSecondaryLabel}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            secondaryOptionsName={secondaryOptionsName}
            title={title}
            currentSelectionForSlot={currentSelectionForSlot}
            handleSecondarySelect={handleSecondarySelect}
            formatPrice={formatPrice}
          />
        )}
      </div>
    </li>
  );
}

/**
 * @param {Object} props
 * @param {Object[]} props.secondaryOptions
 * @param {() => void} props.openModal
 * @param {string} props.selectedSecondaryLabel
 * @param {boolean} props.modalOpen
 * @param {Function} props.setModalOpen
 * @param {string} props.secondaryOptionsName
 * @param {string} props.title
 * @param {string} props.currentSelectionForSlot
 * @param {Function} props.handleSecondarySelect
 * @param {Function} props.formatPrice
 * @returns {import('react').JSX.Element}
 */
export function SecondaryOptions({
  secondaryOptions,
  openModal,
  selectedSecondaryLabel,
  modalOpen,
  setModalOpen,
  secondaryOptionsName,
  title,
  currentSelectionForSlot,
  handleSecondarySelect,
  formatPrice
}) {
  return (
    <>
      <div className={styles['personalisation__selection-variant-choice-wrapper']}>
        <button
          className={styles['personalisation__selection-variant-choice'] + ' body-sm button-reset'}
          onClick={openModal}
          tabIndex={0}
        >
          {selectedSecondaryLabel}
          <span className={styles['svg-wrapper']}>
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.7946 7.20541C15.9573 7.36813 15.9573 7.63195 15.7946 7.79467L10.7946 12.7947C10.6319 12.9574 10.368 12.9574 10.2053 12.7947L5.20529 7.79471C5.04257 7.63199 5.04257 7.36818 5.20529 7.20546C5.36801 7.04274 5.63182 7.04273 5.79454 7.20545L10.4999 11.9108L15.2053 7.20541C15.368 7.04269 15.6319 7.04269 15.7946 7.20541Z"
                fill="#171717"
              />
            </svg>
          </span>
        </button>
      </div>
      <PersonalisationHelperModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={secondaryOptionsName}>
        <fieldset>
          <ul
            className={
              gridStyles['personalisation__selection-grid'] +
              ' ' +
              gridStyles['personalisation__selection-grid--1-column']
            }
          >
            {secondaryOptions.map((option) => (
              <SecondaryOption
                key={option.id}
                option={option}
                currentSelectionForSlot={currentSelectionForSlot}
                handleSecondarySelect={handleSecondarySelect}
                title={title}
                secondaryOptionsName={secondaryOptionsName}
                formatPrice={formatPrice}
              />
            ))}
          </ul>
        </fieldset>
      </PersonalisationHelperModal>
    </>
  );
}

function SecondaryOption({
  option,
  currentSelectionForSlot,
  handleSecondarySelect,
  title,
  secondaryOptionsName,
  formatPrice
}) {
  const handleSecondarySelectOption = useCallback(() => {
    handleSecondarySelect({ optionId: option.id });
  }, [handleSecondarySelect, option.id]);

  return (
    <li
      key={option.id + option.title}
      className={
        styles['list-style-none'] +
        ' ' +
        styles['personalisation__card'] +
        ' ' +
        styles['personalisation__card--horizontal']
      }
      aria-label={title || 'Personalisation option'}
    >
      <input
        readOnly
        id={option.id + option.title}
        type="radio"
        name={option.id + option.title}
        value={option.id}
        checked={currentSelectionForSlot === option.id}
        onChange={handleSecondarySelectOption}
        className={styles['personalisation__hidden-input']}
        disabled={!option.availableForSale}
      />
      <div
        className={
          styles['personalisation__card-inner-wrapper'] +
          ' ' +
          styles['personalisation__card-inner-wrapper--horizontal']
        }
      >
        <label htmlFor={option.id + option.title} className={styles['personalisation__card-inner']}>
          {option.image?.url && (
            <div className={styles['personalisation__card-image-wrapper']}>
              <ResponsiveImage
                src={option.image?.url}
                className={styles['personalisation__card-image']}
                alt={option.image?.altText || title + ' - ' + option.title}
                srcSetSizes={[96]}
                sizes={[]}
                fallbackSize="96px"
                decoding="async"
                loading="lazy"
              />
            </div>
          )}
          <div className={styles['personalisation__card-content']}>
            {option.title && (
              <p className="body-sm font-medium">
                {secondaryOptionsName ? option.title.replace(secondaryOptionsName + ' /', '') : option.title}
              </p>
            )}
            {option.shortDescription?.value && (
              <p className="body-xs">{highlightInlineText(option.shortDescription.value, 'orange')}</p>
            )}
            {typeof option.price?.amount !== 'undefined' &&
              option.price?.amount !== null &&
              option.price?.amount > 0 && (
                <p
                  className={styles['personalisation__card-price'] + ' body-xs'}
                  aria-label={`Price: ${formatPrice({ amount: option.price.amount })}${option.compareAtPrice?.amount ? `, was ${formatPrice({ amount: option.compareAtPrice.amount })}` : ''}${option.originalPrice ? `, original price ${formatPrice({ amount: option.originalPrice.amount })}` : ''}`}
                >
                  {!option.originalPrice && option.compareAtPrice?.amount && (
                    <span
                      className={styles['personalisation__card-compare-price'] + ' body-xs'}
                      aria-label={`Was ${formatPrice({ amount: option.compareAtPrice.amount })}`}
                    >
                      {formatPrice({ amount: option.compareAtPrice.amount })}
                    </span>
                  )}
                  {option.originalPrice && (
                    <span
                      className={styles['personalisation__card-original-price'] + ' body-xs'}
                      aria-label={`Original price ${formatPrice({ amount: option.originalPrice.amount })}`}
                    >
                      {formatPrice({ amount: option.originalPrice.amount })}
                    </span>
                  )}
                  <span aria-label={`Additional cost ${formatPrice({ amount: option.price.amount })}`}>
                    +{formatPrice({ amount: option.price.amount })}
                  </span>
                </p>
              )}
          </div>
        </label>
      </div>
    </li>
  );
}
